import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Loader2, XCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) pollStatus(0);
  }, [sessionId]);

  const pollStatus = async (attempt) => {
    if (attempt >= 5) {
      setStatus('timeout');
      return;
    }
    try {
      const res = await api.get(`/api/payments/checkout/status/${sessionId}`);
      setPaymentData(res.data);
      if (res.data.payment_status === 'paid') {
        setStatus('paid');
      } else if (res.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollStatus(attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  };

  const formatCurrency = (cents) => {
    const amount = typeof cents === 'number' && cents > 100 ? cents / 100 : cents;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (!sessionId) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">No Payment Session</h2>
          <Link to="/crm"><Button variant="outline">Back to Dashboard</Button></Link>
        </div>
      </div>
    );
  }

  if (status === 'checking') {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#014DB7] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Verifying Payment...</h2>
          <p className="text-gray-500">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (status === 'paid') {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
          <p className="text-gray-600 mb-4">
            Your payment of {formatCurrency(paymentData?.amount_total)} has been processed.
          </p>
          {paymentData?.transaction_type === 'invoice' && paymentData?.metadata?.initial_quote_payment === 'true' && (
            <p className="text-sm text-blue-600 mb-4">Signed quote total has been paid. Recurring billing will resume on the next cycle.</p>
          )}
          {paymentData?.transaction_type === 'invoice' && paymentData?.metadata?.initial_quote_payment !== 'true' && (
            <p className="text-sm text-blue-600 mb-4">Invoice has been marked as paid.</p>
          )}
          <Link to="/crm">
            <Button className="bg-[#014DB7]">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="text-center">
        <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Issue</h2>
        <p className="text-gray-500 mb-4">
          {status === 'expired' ? 'Payment session expired.' : 'Could not verify payment. Please check your email for confirmation.'}
        </p>
        <Link to="/crm"><Button variant="outline">Back to Dashboard</Button></Link>
      </div>
    </div>
  );
}
